// src/pages/InvoiceDetails.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    List, User, Hash, Calendar, DollarSign, Clock, Mail, Printer,
    MoreVertical, FileText, Ban, Receipt, ChevronLeft, Search,
    CheckSquare, Square, Loader2, Menu, X
} from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Dropdown from '../components/ui/Dropdown';
import { useToast } from '../hooks/useToast';
import { fetchInvoiceForStaff } from '../apiHelper';
import InvoicePDFTemplate from './InvoicePDFTemplate';
import FastAPIClient from '../services/apiClient';

const client = new FastAPIClient();

const PrintButton = ({ invoice, loading }) => {
    const componentRef = useRef();

    const handlePrint = () => {
        if (loading || !invoice) return;

        const printWindow = window.open('', '_blank');

        printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Invoice ${invoice.idempotency_key}</title>
        <script src="https://cdn.tailwindcss.com"></script>
        <link href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600;700&display=swap" rel="stylesheet">
        <style>
          body { margin: 0; padding: 0; background: white; }
          @page { size: A4; margin: 0; }
          @media print {
            body { -webkit-print-color-adjust: exact; color-adjust: exact; }
            .print-container { padding: 40px; }
          }
          body, * { font-family: 'Open Sans', system-ui, sans-serif !important; }
        </style>
      </head>
      <body class="print-container bg-white">
        <div class="max-w-[210mm] mx-auto bg-white min-h-[297mm] shadow-none">
          ${componentRef.current.innerHTML}
        </div>
      </body>
      </html>
    `);

        printWindow.document.close();
        printWindow.focus();

        printWindow.onload = () => {
            setTimeout(() => {
                printWindow.print();
                printWindow.close();
            }, 300);
        };
    };

    return (
        <>
            {/* Hidden printable version - exact copy of what user sees */}
            <div className="hidden">
                <div ref={componentRef}>
                    <InvoicePDFTemplate invoice={invoice} loading={false} />
                </div>
            </div>

            <Button
                variant="secondary"
                className="flex items-center gap-2"
                onClick={handlePrint}
                disabled={loading || !invoice}
            >
                <Printer size={18} />
                Print PDF
            </Button>
        </>
    );
};

const InvoiceDetails = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { showToast } = useToast();
    const listRef = useRef(null);

    const { selectedInvoice, allInvoices = [] } = location.state || {};
    const [activeInvoiceId, setActiveInvoiceId] = useState(selectedInvoice?.orders_id);
    const [searchTerm, setSearchTerm] = useState('');
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false); // Mobile drawer
    const [user, setUser] = useState(null);
    const [detailedInvoice, setDetailedInvoice] = useState(null);
    const [loadingInvoice, setLoadingInvoice] = useState(false);

    // Load logged-in user
    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            showToast({ message: "Please login to view invoices.", show: true, toastType: 0 });
            navigate("/login");
            return;
        }
        try {
            const parsedUser = JSON.parse(lcUser);
            setUser(parsedUser);
        } catch {
            showToast({ message: "Invalid session.", show: true, toastType: 2 });
            navigate("/login");
        }
    }, [showToast, navigate]);

    const currentInvoice = useMemo(() => {
        return allInvoices.find(inv => inv.orders_id === activeInvoiceId) || selectedInvoice;
    }, [allInvoices, activeInvoiceId, selectedInvoice]);

    // Fetch full invoice details
    useEffect(() => {
        if (!currentInvoice?.orders_id || !user?.users_id) return;

        const loadInvoice = async () => {
            setLoadingInvoice(true);
            try {
                const data = await fetchInvoiceForStaff({
                    users_id: user.users_id,
                    orders_id: currentInvoice.orders_id
                });
                console.log(data)
                setDetailedInvoice(data);
            } catch (err) {
                console.error(err);
                showToast({ message: "Failed to load invoice details", show: true, toastType: 2 });
            } finally {
                setLoadingInvoice(false);
            }
        };

        loadInvoice();
    }, [currentInvoice?.orders_id, user?.users_id]);

    const filteredInvoices = useMemo(() => {
        if (!searchTerm.trim()) return allInvoices;
        const lower = searchTerm.toLowerCase();
        return allInvoices.filter(inv =>
            inv.customer.name.toLowerCase().includes(lower) ||
            inv.idempotency_key.toLowerCase().includes(lower)
        );
    }, [allInvoices, searchTerm]);

    const handleInvoiceSelect = useCallback((id) => {
        setActiveInvoiceId(id);
        setMobileSidebarOpen(false); // Close drawer on mobile after selection
    }, []);


    const createCreditNote = useCallback(() => {
        console.log(currentInvoice)
        navigate('/sales/credit-notes/new', {
            state: {
                invoice_number: currentInvoice.idempotency_key,
                store_id: currentInvoice.store.store_id,
            }
        })
    }, [currentInvoice])

    useEffect(() => {
        if (activeInvoiceId && listRef.current) {
            const el = listRef.current.querySelector(`[data-id="${activeInvoiceId}"]`);
            el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [activeInvoiceId]);

    // useEffect(() => {
    //     if (currentInvoice) {
    //         console.log('Active invoice updated: ', currentInvoice);
    //     }
    // }, [currentInvoice]);

    const handleVoidInvoice = useCallback(async () => {
        if(!detailedInvoice) {
            showToast("Invoice details not loaded yet", "error");
            return;
        }

        const payload = {
            orderId: detailedInvoice.orders_id,
            receiptNumber: detailedInvoice.idempotency_key,
            customer_id: detailedInvoice.customer.member_id,
            storeId: detailedInvoice.store.store_id,
        }

        try {
            const response = await client.postData("/sales/void-invoice", payload)
            // console.log(response)
            showToast(response.msg, 'success')
        } catch (err) {
            const errorMessage = err.response?.data?.detail || `Failed to void invoice.`
            showToast(errorMessage, "error")
        }


    },[detailedInvoice, showToast])

    const formatDate = (dateStr) => {
        return dateStr ? new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
    };

    if (!allInvoices.length || !currentInvoice) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-orange-600'>
                <Ban size={32} className="mb-4" />
                <p className="text-lg mb-4">No invoice data available.</p>
                <Button onClick={() => navigate('/sales/invoices')} variant="primary">
                    <ChevronLeft size={16} className="mr-2" /> Back to Invoices
                </Button>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-[calc(100vh-100px)] bg-gray-100">
            {/* Mobile Header */}
            <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-30">
                <Button variant="ghost" size="sm" onClick={() => navigate('/sales/invoices')}>
                    <ChevronLeft size={20} />
                </Button>
                <h1 className="text-lg font-bold text-text-primary truncate px-4">
                    Invoice {currentInvoice.idempotency_key}
                </h1>
                <Button variant="ghost" size="sm" onClick={() => setMobileSidebarOpen(true)}>
                    <Menu size={22} />
                </Button>
            </div>

            <div className="flex flex-1 overflow-hidden">
                {/* Mobile Sidebar Drawer */}
                <div className={`fixed inset-0 z-40 lg:hidden transition-transform duration-300 ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
                    <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setMobileSidebarOpen(false)} />
                    <div className="absolute left-0 top-0 h-full w-full max-w-sm bg-white shadow-2xl flex flex-col">
                        <div className="flex justify-between items-center p-4 border-b border-gray-200">
                            <h2 className="text-lg font-bold flex items-center gap-2">
                                <List size={20} /> Invoices ({allInvoices.length})
                            </h2>
                            <Button variant="ghost" size="sm" onClick={() => setMobileSidebarOpen(false)}>
                                <X size={20} />
                            </Button>
                        </div>

                        <div className="p-4 border-b border-gray-200">
                            <Input
                                type="text"
                                placeholder="Search invoices..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2 text-sm"
                                icon={<Search size={18} className="text-gray-dark absolute left-3 top-1/2 -translate-y-1/2" />}
                            />
                        </div>

                        <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3">
                            {filteredInvoices.map(inv => (
                                <div
                                    key={inv.orders_id}
                                    data-id={inv.orders_id}
                                    onClick={() => handleInvoiceSelect(inv.orders_id)}
                                    className={`p-4 rounded-lg cursor-pointer transition-all border ${inv.orders_id === activeInvoiceId
                                        ? 'bg-gmail-blue text-white border-gmail-blue shadow-md'
                                        : 'hover:bg-gray-50 border-gray-200'
                                        }`}
                                >
                                    <div className="flex items-center gap-3 mb-2">
                                        {inv.orders_id === activeInvoiceId ? <CheckSquare size={16} /> : <Square size={14} className="text-gray-400" />}
                                        <div>
                                            <p className={`font-bold text-sm ${inv.orders_id === activeInvoiceId ? 'text-white' : 'text-text-primary'}`}>
                                                {inv.customer.name}
                                            </p>
                                            <p className={`text-xs ${inv.orders_id === activeInvoiceId ? 'text-gray-100' : 'text-text-secondary'}`}>
                                                {inv.idempotency_key}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3 text-xs mt-3">
                                        <div>
                                            <p className="text-secondary">Amount</p>
                                            <p className={`font-bold ${inv.orders_id === activeInvoiceId ? 'text-white' : ''}`}>
                                                {inv.balance_due_formatted}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-secondary">Date</p>
                                            <p className={inv.orders_id === activeInvoiceId ? 'text-white' : ''}>
                                                {formatDate(inv.placed_on)}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="mt-3">
                                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${inv.status.color}`}>
                                            {inv.status.label}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Desktop Sidebar */}
                <div className="hidden lg:block w-full lg:w-1/3 xl:w-1/4">
                    <Card className="h-full bg-white rounded-lg shadow-md flex flex-col">
                        <div className="flex justify-between items-center p-4 border-b border-gray-200">
                            <h2 className="text-xl font-bold flex items-center gap-2">
                                <List size={20} className="text-gmail-blue" /> All Invoices ({allInvoices.length})
                            </h2>
                            <Button variant="ghost" size="sm" onClick={() => navigate('/sales/invoices')}>
                                <ChevronLeft size={20} />
                            </Button>
                        </div>

                        <div className="p-4">
                            <Input
                                type="text"
                                placeholder="Search invoices..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2 text-sm"
                                icon={<Search size={18} className="text-gray-dark absolute left-3 top-1/2 -translate-y-1/2" />}
                            />
                        </div>

                        <div ref={listRef} className="flex-1 overflow-y-auto space-y-2 px-4 pb-4">
                            {filteredInvoices.map(inv => (
                                <div
                                    key={inv.orders_id}
                                    data-id={inv.orders_id}
                                    onClick={() => handleInvoiceSelect(inv.orders_id)}
                                    className={`p-4 rounded-lg cursor-pointer transition-all border ${inv.orders_id === activeInvoiceId
                                        ? 'bg-gmail-blue text-white border-gmail-blue shadow-md'
                                        : 'hover:bg-gray-50 border-gray-200'
                                        }`}
                                >
                                    {/* Same content as mobile */}
                                    <div className="flex items-center gap-3 mb-2">
                                        {inv.orders_id === activeInvoiceId ? <CheckSquare size={16} /> : <Square size={14} className="text-gray-400" />}
                                        <div>
                                            <p className={`font-bold text-sm ${inv.orders_id === activeInvoiceId ? 'text-white' : 'text-text-primary'}`}>
                                                {inv.customer.name}
                                            </p>
                                            <p className={`text-xs ${inv.orders_id === activeInvoiceId ? 'text-gray-100' : 'text-text-secondary'}`}>
                                                {inv.idempotency_key}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3 text-xs mt-3">
                                        <div><p className="text-secondary">Amount</p><p className={`font-bold ${inv.orders_id === activeInvoiceId ? 'text-white' : ''}`}>{inv.balance_due_formatted}</p></div>
                                        <div><p className="text-secondary">Date</p><p className={inv.orders_id === activeInvoiceId ? 'text-white' : ''}>{formatDate(inv.placed_on)}</p></div>
                                    </div>
                                    <div className="mt-3">
                                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${inv.status.color}`}>
                                            {inv.status.label}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>
                </div>

                {/* Main Detail Pane */}
                <div className="flex-1 bg-white rounded-lg shadow-md flex flex-col overflow-hidden">
                    {/* Header */}
                    <div className="p-6 border-b border-gray-200">
                        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                            <div>
                                <h1 className="text-2xl lg:text-3xl font-bold text-text-primary flex items-center gap-3">
                                    <Hash size={28} className="text-gmail-blue" />
                                    {currentInvoice.idempotency_key}
                                </h1>
                                <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-text-secondary">
                                    <div className="flex items-center gap-2">
                                        <User size={16} /> <span className="font-medium text-text-primary">{currentInvoice.customer.name}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Calendar size={16} /> Placed on {formatDate(currentInvoice.placed_on)}
                                    </div>
                                    {currentInvoice.due_date && (
                                        <div className="flex items-center gap-2">
                                            <Clock size={16} /> Due {formatDate(currentInvoice.due_date)}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-2">
                                <Button variant="primary" className="flex items-center gap-2">
                                    <Mail size={18} /> Send Email
                                </Button>
                                <PrintButton invoice={detailedInvoice} loading={loadingInvoice} />
                                <Button
                                    variant="primary"
                                    className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
                                    onClick={() => navigate('/sales/invoices/receive-payment', {
                                        state: {
                                            creditsale_id: currentInvoice.creditsale_id || currentInvoice.orders_id,
                                            orders_id: currentInvoice.orders_id,
                                            customer_id: currentInvoice.customer.member_id,
                                            invoice_number: currentInvoice.idempotency_key,
                                            store_id: currentInvoice.store.store_id,
                                            currency: currentInvoice.currency_code
                                        }
                                    })}
                                >
                                    <DollarSign size={18} /> Receive Payment
                                </Button>
                                <Dropdown
                                    trigger={<Button variant="ghost" size="sm" className="p-2"><MoreVertical size={20} /></Button>}
                                    align="right"
                                >
                                    <button onClick={createCreditNote} className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-100 flex items-center gap-3"><Receipt size={16} /> Create Credit Note</button>
                                    <button className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-100 flex items-center gap-3"><FileText size={16} /> View Journal</button>
                                    <hr className="my-1 border-gray-200" />
                                    <button 
                                        onClick={handleVoidInvoice}
                                        className="w-full text-left px-4 py-2.5 text-sm hover:bg-red-50 text-red-600 flex items-center gap-3">
                                            <Ban size={16} /> Void Invoice</button>
                                </Dropdown>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mt-6 gap-4">
                            <span className={`inline-block px-5 py-2 rounded-full text-sm font-bold ${currentInvoice.status.color}`}>
                                {currentInvoice.status.label}
                            </span>
                            <div className="text-right">
                                <p className="text-sm text-text-secondary">Balance Due</p>
                                <p className="text-2xl lg:text-3xl font-bold text-gmail-blue">
                                    {currentInvoice.balance_due_formatted}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Content WORK HERE */}
                    <div className="flex-1 overflow-y-auto bg-gray-50 p-4 lg:p-8">
                        <div className="max-w-4xl mx-auto">
                            <InvoicePDFTemplate invoice={detailedInvoice} loading={loadingInvoice} />
                        </div>
                    </div>
                    {/* <div className="flex-1 p-6 lg:p-8 overflow-y-auto">
                        <div className="text-center py-20 text-text-secondary">
                            <FileText size={64} className="mx-auto mb-6 text-gray-300" />
                            <h3 className="text-xl font-semibold mb-2">Invoice Details View</h3>
                            <p>Full invoice line items, payments, notes, PDF preview coming soon...</p>
                        </div>
                    </div> */}
                </div>
            </div>
        </div>
    );
};

export default InvoiceDetails;